import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import StoreIcon from '@mui/icons-material/Store';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { format } from 'date-fns';
import ReactJson from '@microlink/react-json-view';
import {
  getSafeJson,
  getLevelColor,
  formatMs,
  formatMsToSeconds,
} from '../pages/logsPage.utils';

export default function LogsDetailModal({
  openModal,
  handleCloseModal,
  selectedLog,
  selectedMetadata,
  selectedQueueInfo,
  selectedStageTimings,
  selectedPhone,
  selectedRestauranteId,
  setFilterTraceId,
}) {
  return (
    <Dialog
      open={openModal}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(15,23,42,0.06)', pb: 1.5 }}>
        <Typography fontWeight={700} sx={{ color: '#0f172a', fontSize: '1rem' }}>Detalhes do Log</Typography>
        <Chip
          label={selectedLog?.level}
          color={getLevelColor(selectedLog?.level)}
          size="small"
          sx={{ fontWeight: 700, borderRadius: '10px' }}
        />
      </DialogTitle>
      <DialogContent dividers>
        {selectedLog && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Mensagem</Typography>
              <Typography variant="body1">{selectedLog.message}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Data/Hora</Typography>
                <Typography variant="body2">{format(new Date(selectedLog.created_at), 'dd/MM/yyyy HH:mm:ss.SSS')}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Etapa</Typography>
                <Typography variant="body2">{selectedLog.step}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Trace ID</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">{selectedLog.trace_id || '-'}</Typography>
                  {selectedLog.trace_id && (
                    <Button size="small" variant="outlined" onClick={() => setFilterTraceId(selectedLog.trace_id)} sx={{ borderRadius: '8px' }}>
                      Filtrar
                    </Button>
                  )}
                </Stack>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Telefone</Typography>
                <Typography variant="body2">{selectedPhone || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Restaurante ID</Typography>
                <Typography variant="body2">{selectedRestauranteId || '-'}</Typography>
              </Box>
              {selectedMetadata?.ignored && (
                <Box>
                  <Chip label="IGNORADO" color="error" size="small" sx={{ borderRadius: '10px', fontWeight: 700 }} />
                </Box>
              )}
            </Box>

            {/* AI Request/Response Visualization */}
            {selectedLog.step.startsWith('GEMINI') && (
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '12px', p: 2, bgcolor: '#f8f9fa' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                  <SmartToyIcon sx={{ mr: 1, color: '#7b1fa2' }} />
                  Integração Gemini AI
                </Typography>

                {selectedMetadata?.contents && (
                  <Box sx={{ mb: 2 }}>
                    <Chip label="Request (Envio)" size="small" color="primary" sx={{ mb: 1, borderRadius: '10px' }} />
                    <Paper variant="outlined" sx={{ p: 1, bgcolor: '#fff', maxHeight: 300, overflow: 'auto', borderRadius: '10px' }}>
                      <ReactJson
                        src={getSafeJson(selectedMetadata.contents)}
                        name={false}
                        displayDataTypes={false}
                        enableClipboard={true}
                        collapsed={2}
                      />
                    </Paper>
                  </Box>
                )}

                {selectedMetadata?.response && (
                  <Box>
                    <Chip label="Response (Resposta)" size="small" color="secondary" sx={{ mb: 1, borderRadius: '10px' }} />
                    <Paper variant="outlined" sx={{ p: 1, bgcolor: '#fff', maxHeight: 300, overflow: 'auto', borderRadius: '10px' }}>
                      {typeof selectedMetadata.response === 'object' ? (
                        <ReactJson
                          src={getSafeJson(selectedMetadata.response)}
                          name={false}
                          displayDataTypes={false}
                          enableClipboard={true}
                          collapsed={2}
                        />
                      ) : (
                        <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                          {selectedMetadata.response}
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                )}
              </Box>
            )}

            {/* Z-API Visualization */}
            {selectedLog.step.startsWith('ZAPI') && (
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '12px', p: 2, bgcolor: '#f8f9fa' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                  <PhoneIphoneIcon sx={{ mr: 1, color: '#2e7d32' }} />
                  Integração Z-API (WhatsApp)
                </Typography>

                {selectedMetadata?.messageToSend && (
                  <Box sx={{ mb: 2 }}>
                    <Chip label="Request (Envio)" size="small" color="success" sx={{ mb: 1, borderRadius: '10px' }} />
                    <Paper variant="outlined" sx={{ p: 1, bgcolor: '#fff', maxHeight: 300, overflow: 'auto', borderRadius: '10px' }}>
                      <ReactJson
                        src={getSafeJson(selectedMetadata.messageToSend)}
                        name={false}
                        displayDataTypes={false}
                        enableClipboard={true}
                        collapsed={2}
                      />
                    </Paper>
                  </Box>
                )}

                {selectedMetadata?.response && (
                  <Box>
                    <Chip label="Response (Resposta)" size="small" color="info" sx={{ mb: 1, borderRadius: '10px' }} />
                    <Paper variant="outlined" sx={{ p: 1, bgcolor: '#fff', maxHeight: 300, overflow: 'auto', borderRadius: '10px' }}>
                      <ReactJson
                        src={getSafeJson(selectedMetadata.response)}
                        name={false}
                        displayDataTypes={false}
                        enableClipboard={true}
                        collapsed={2}
                      />
                    </Paper>
                  </Box>
                )}
              </Box>
            )}

            {/* Sischef Visualization */}
            {selectedLog.step.startsWith('SISCHEF') && (
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '12px', p: 2, bgcolor: '#f8f9fa' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                  <StoreIcon sx={{ mr: 1, color: '#ed6c02' }} />
                  Integração Sischef
                </Typography>

                {selectedMetadata?.pedido && (
                  <Box sx={{ mb: 2 }}>
                    <Chip label="Request (Envio)" size="small" color="warning" sx={{ mb: 1, borderRadius: '10px' }} />
                    <Paper variant="outlined" sx={{ p: 1, bgcolor: '#fff', maxHeight: 300, overflow: 'auto', borderRadius: '10px' }}>
                      <ReactJson
                        src={getSafeJson(selectedMetadata.pedido)}
                        name={false}
                        displayDataTypes={false}
                        enableClipboard={true}
                        collapsed={2}
                      />
                    </Paper>
                  </Box>
                )}

                {selectedMetadata?.response && (
                  <Box>
                    <Chip label="Response (Resposta)" size="small" color="info" sx={{ mb: 1, borderRadius: '10px' }} />
                    <Paper variant="outlined" sx={{ p: 1, bgcolor: '#fff', maxHeight: 300, overflow: 'auto', borderRadius: '10px' }}>
                      <ReactJson
                        src={getSafeJson(selectedMetadata.response)}
                        name={false}
                        displayDataTypes={false}
                        enableClipboard={true}
                        collapsed={2}
                      />
                    </Paper>
                  </Box>
                )}
              </Box>
            )}

            {(selectedLog.step.startsWith('QUEUE') || selectedLog.step.startsWith('PROCESSOR') || selectedLog.step.startsWith('AI_DISPATCH') || selectedQueueInfo.queue.length > 0) && (
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '12px', p: 2, bgcolor: '#f8f9fa' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                  <AccessTimeIcon sx={{ mr: 1, color: '#1565c0' }} />
                  Fila e debounce
                </Typography>
                <Grid container spacing={2} sx={{ mb: selectedQueueInfo.queue.length ? 2 : 0 }}>
                  {selectedQueueInfo.delayMs !== null && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">Delay configurado</Typography>
                      <Typography variant="body2">{formatMsToSeconds(selectedQueueInfo.delayMs)} s</Typography>
                    </Grid>
                  )}
                  {selectedQueueInfo.remainingMs !== null && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">Tempo restante</Typography>
                      <Typography variant="body2">{formatMsToSeconds(selectedQueueInfo.remainingMs)} s</Typography>
                    </Grid>
                  )}
                  {selectedQueueInfo.elapsedMs !== null && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">Tempo decorrido</Typography>
                      <Typography variant="body2">{formatMsToSeconds(selectedQueueInfo.elapsedMs)} s</Typography>
                    </Grid>
                  )}
                  {selectedQueueInfo.scheduledFor && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">Agendado para</Typography>
                      <Typography variant="body2">{format(new Date(selectedQueueInfo.scheduledFor), 'dd/MM HH:mm:ss')}</Typography>
                    </Grid>
                  )}
                  {selectedQueueInfo.traceIds?.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Traces no lote</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {selectedQueueInfo.traceIds.map((trace) => (
                          <Chip key={trace} label={trace} size="small" onClick={() => setFilterTraceId(trace)} sx={{ borderRadius: '10px' }} />
                        ))}
                      </Stack>
                    </Grid>
                  )}
                </Grid>
                {selectedQueueInfo.queue.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 1, bgcolor: '#fff', maxHeight: 300, overflow: 'auto', borderRadius: '10px' }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Itens na fila (com trace_id)</Typography>
                    <ReactJson
                      src={selectedQueueInfo.queue}
                      name={false}
                      displayDataTypes={false}
                      enableClipboard={true}
                      collapsed={1}
                    />
                  </Paper>
                )}
              </Box>
            )}

            {(selectedLog.step.startsWith('GEMINI') || selectedLog.step.startsWith('AI_DISPATCH') || selectedMetadata?.prep_duration_ms || selectedMetadata?.elapsed_since_dispatch_ms || selectedStageTimings.length > 0) && (
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '12px', p: 2, bgcolor: '#f8f9fa' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                  <SmartToyIcon sx={{ mr: 1, color: '#3949ab' }} />
                  Métricas IA/Gemini
                </Typography>
                <Grid container spacing={2}>
                  {(selectedMetadata?.elapsed_since_dispatch_ms !== undefined || selectedMetadata?.elapsed_since_dispatch_ms !== null) && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">Elapsed desde dispatch</Typography>
                      <Typography variant="body2">{formatMs(selectedMetadata?.elapsed_since_dispatch_ms)}</Typography>
                    </Grid>
                  )}
                  {(selectedMetadata?.prep_duration_ms !== undefined && selectedMetadata?.prep_duration_ms !== null) && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">Prep Gemini</Typography>
                      <Typography variant="body2">{formatMs(selectedMetadata?.prep_duration_ms)}</Typography>
                    </Grid>
                  )}
                  {selectedMetadata?.dispatch_started_at && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">Dispatch started</Typography>
                      <Typography variant="body2">{new Date(selectedMetadata.dispatch_started_at).toLocaleTimeString()}</Typography>
                    </Grid>
                  )}
                  {selectedMetadata?.attempt_started_at && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">Attempt started</Typography>
                      <Typography variant="body2">{new Date(selectedMetadata.attempt_started_at).toLocaleTimeString()}</Typography>
                    </Grid>
                  )}
                  {selectedMetadata?.order_key_ms && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">Order key</Typography>
                      <Typography variant="body2">{selectedMetadata.order_key_ms}</Typography>
                    </Grid>
                  )}
                </Grid>
                {selectedStageTimings.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Estágios de preparação</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {selectedStageTimings.map((stage) => (
                        <Chip
                          key={stage.stage}
                          label={`${stage.stage}: ${formatMs(stage.duration_ms)}`}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: '10px', fontWeight: 700, letterSpacing: 0.3 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Metadata Completo (JSON)</Typography>
              </Box>
              <Paper
                variant="outlined"
                sx={{ p: 2, bgcolor: '#fff', maxHeight: 400, overflow: 'auto', borderRadius: '12px' }}
              >
                <ReactJson
                  src={selectedMetadata}
                  name={false}
                  displayDataTypes={false}
                  enableClipboard={true}
                  collapsed={false}
                  theme="rjv-default"
                />
              </Paper>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid rgba(15,23,42,0.06)', px: 3, py: 1.5 }}>
        <Button
          onClick={handleCloseModal}
          sx={{ borderRadius: '10px', fontWeight: 600, color: '#475569', '&:hover': { bgcolor: 'rgba(99,102,241,0.08)', color: '#6366f1' } }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
